import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';

interface HtmlComponentProps {
    html?: string | null;
}

const HtmlComponent: React.FC<HtmlComponentProps> = ({ html }) => {
    const contentRef = useRef<HTMLSpanElement>(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!contentRef.current) {
            return;
        }

        const links = contentRef.current.querySelectorAll('a');
        const handleClick = (event: MouseEvent) => {
            const target = event.target as HTMLAnchorElement;
            const href = target.getAttribute('href');
            if (!href) {
                return;
            }

            // Check if the href starts with 'http:' or 'https:'
            if (href.startsWith('http://') || href.startsWith('https://')) {
                // It's a fully qualified link, force such to open in a new tab/window:
                event.preventDefault();
                window.open(href, '_blank');
            } else if (href.startsWith('#')) {
                // It's a hash link, do nothing
                // These hash links are part of the articles service Table of Contents
                return;
            } else {
                // It's a relative link, intercept and use navigate
                event.preventDefault();
                navigate(href);
            }
        };

        links.forEach((link) => {
            link.addEventListener('click', handleClick as EventListener);
        });

        return () => {
            links.forEach((link) => {
                link.removeEventListener('click', handleClick as EventListener);
            });
        };
    }, [navigate]);

    if (!html) {
        return null;
    }

    const createMarkup = (htmlString: string) => {
        DOMPurify.addHook('uponSanitizeElement', function (node, data) {
            if (data.tagName === 'iframe') {
                // Check if the iframe's `src` attribute matches the allowed pattern
                const el = node as Element;
                const srcAttr = el.getAttribute('src');
                if (!srcAttr) {
                    //  Remove iframe if no src attribute
                    return node.parentNode?.removeChild(node);
                }
                // Change this to use startsWith where possible
                if (
                    !srcAttr.startsWith('https://www.youtube.com/embed/') &&
                    !srcAttr.startsWith(
                        'https://www.youtube-nocookie.com/embed/'
                    ) &&
                    !srcAttr.startsWith('https://youtube.com/embed/') &&
                    !srcAttr.startsWith(
                        'https://youtube-nocookie.com/embed/'
                    ) &&
                    !srcAttr.startsWith('https://youtu.be/embed/') &&
                    !srcAttr.startsWith('https://player.vimeo.com/video') &&
                    !srcAttr.startsWith('https://js.storylane.io/js') &&
                    !srcAttr.startsWith('https://app.storylane.io/share/')
                ) {
                    // Remove iframe if src attribute doesn't match explicit url pattern from the above sources
                    return node.parentNode?.removeChild(node);
                }

                // Adds title which is required for accessibility
                const title = el.getAttribute('title');
                if (!title) {
                    el.setAttribute('title', 'Embedded content');
                }

                // Add security attributes and youtube fullscreen
                el.setAttribute('sandbox', 'allow-scripts allow-same-origin');
                el.setAttribute('allowfullscreen', '');
                el.setAttribute(
                    'referrerpolicy',
                    'strict-origin-when-cross-origin'
                );
            }
        });

        return {
            __html: DOMPurify.sanitize(htmlString, {
                ADD_TAGS: ['iframe'],
                ADD_ATTR: ['sandbox', 'allowfullscreen', 'referrerpolicy']
            })
        };
    };

    return (
        <span ref={contentRef} dangerouslySetInnerHTML={createMarkup(html)} />
    );
};

export default HtmlComponent;
